import useIterateModifiers from '@/hooks/useIterateModifiers';
import _find from 'lodash/find';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';

// import components
import CartVatRow from '@/components/organisms/CartVatRow';
import ButtonActionsBox from '@/components/organisms/OrderProductBox/ButtonActionsBox';
import PriceBox from '@/components/organisms/OrderProductBox/PriceBox';
import QuantityBox from '@/components/organisms/OrderProductBox/QuantityBox';
import RegisteredProductBox from '@/components/organisms/OrderProductBox/RegisteredProductBox';
import RatingStars from '@/components/organisms/RatingStars';

import { hasImageSizes } from '@/utils/Common/Utils';
import CONST from '@/utils/Constants/General';
import { format } from 'date-fns';
import WeeeTax from './WeeeTax';

const OrderProductBox = ({
  modifiers,
  product,
  removeReview,
  readReview,
  writeReview,
  editEntry,
  removeEntry,
  isReview,
  isRegistered,
  shippingDate,
  simpleView,
  showCode,
  indexRatingStar,
  isReturn,
  expectedQuantity,
  printLabelBtn,
  printFormBtn,
  handleHowItWorksBtn,
  imageFormat,
  isOrderFlow,
  hasRecipes,
}) => {
  const configurableProductImg = product?.configurableProductDataSnapshot;
  const modifiersString = useIterateModifiers('dln-orderRev--', modifiers);
  const { t } = useTranslation();
  const productImage = isOrderFlow
    ? product.productImage
    : _find(product.productImages, { format: imageFormat }) ||
      (product.productImages && product.productImages[0]);

  const commentReview = comment => {
    if (comment.length > CONST.GENERAL.MAX_STRING) {
      return `${comment.substring(0, CONST.GENERAL.MAX_STRING)}...`;
    }
    return comment;
  };

  return (
    <div className={`dln-orderRev ${modifiersString}`}>
      {(hasImageSizes(productImage) || configurableProductImg) && (
        <picture className='dln-image dln-orderRev__itemPic'>
          <source
            srcSet={configurableProductImg || productImage.l.url}
            media='(min-width:901px)'
          />
          <source
            srcSet={configurableProductImg || productImage.m.url}
            media='(min-width:600px)'
          />
          <img
            className='dln-image__image'
            srcSet={configurableProductImg || productImage.s.url}
            alt={productImage.altText}
          />
        </picture>
      )}
      <div className='dln-orderRev__content'>
        <div className='dln-orderRev__details'>
          <div className='dln-orderRev__wrap dln-orderRev__wrap--main'>
            {isReview ? (
              <RatingStars
                id={product.productCode || product.id}
                currValue={product.rating}
                name={`${product.productCode || product.id}-ratingstars`}
                disabled
                modifiers={['disabled']}
                indexRatingStar={indexRatingStar}
              />
            ) : null}
            <h3 className='dln-orderRev__detailsTitle'>
              {product.productName}
            </h3>
            {product.productCode && showCode && (
              <p className='dln-orderRev__productCode'>{product.productCode}</p>
            )}
            {isReview && (
              <p className='dln-orderRev__desc'>
                {commentReview(product.comment)}
              </p>
            )}
            {!simpleView && (
              <>
                <PriceBox
                  actualPriceValue={product.actualPriceValue}
                  actualPrice={product.actualPrice}
                  oldPriceValue={product.oldPriceValue}
                  oldPrice={product.oldPrice}
                  isRegistered={isRegistered}
                  isReview={isReview}
                />
                {!isReview && !isRegistered && (
                  <CartVatRow vat={product.vat} vatRatio={product.vatratio} />
                )}
                <WeeeTax weeeTax={product.entryWeeeTax} />
              </>
            )}
          </div>
          {!simpleView && (
            <QuantityBox quantity={expectedQuantity || product.quantity} />
          )}
          {shippingDate && (
            <div className='dln-orderRev__wrap dln-orderRev__wrap--delivery'>
              <span className='dln-orderRev__delivery'>
                {t('account:account.order.history.shipped.on', {
                  date: format(shippingDate, t('format.date2')),
                })}
              </span>
            </div>
          )}
          <RegisteredProductBox
            registeredproduct={isRegistered}
            purchasedon={product.purchaseDate}
            registeredon={product.registrationDate}
            t={t}
          />
        </div>
        <div className='dln-orderRev__actions'>
          <ButtonActionsBox
            isProductReview={isReview}
            isRegistered={isRegistered}
            removeReview={e => removeReview(e, product)}
            readReview={e => readReview(e, product)}
            simpleView={simpleView}
            t={t}
            writeReview={e => writeReview(e, product)}
            editEntry={() => editEntry(product)}
            removeEntry={e => removeEntry(e, product)}
            alreadyReviewed={product.alreadyReviewed}
            fakeProduct={product.isRegisteredProductFake}
            isReturn={isReturn}
            printLabelBtn={printLabelBtn}
            printFormBtn={printFormBtn}
            handleHowItWorksBtn={handleHowItWorksBtn}
            isOrderFlow={isOrderFlow}
            isReviewable={product.isReviewable}
            hasRecipes={hasRecipes}
            automaticProductRegistration={product.automaticProductRegistration}
          />
        </div>
      </div>
    </div>
  );
};

OrderProductBox.propTypes = {
  modifiers: PropTypes.arrayOf(PropTypes.string),
  product: PropTypes.shape().isRequired,
  removeReview: PropTypes.func,
  readReview: PropTypes.func,
  writeReview: PropTypes.func,
  editEntry: PropTypes.func,
  removeEntry: PropTypes.func,
  isReview: PropTypes.bool,
  isRegistered: PropTypes.bool,
  simpleView: PropTypes.bool,
  showCode: PropTypes.bool,
  shippingDate: PropTypes.number,
  indexRatingStar: PropTypes.number,
  isReturn: PropTypes.bool,
  isOrderFlow: PropTypes.bool,
  printLabelBtn: PropTypes.string,
  printFormBtn: PropTypes.string,
  expectedQuantity: PropTypes.number,
  handleHowItWorksBtn: PropTypes.func,
  imageFormat: PropTypes.string,
  hasRecipes: PropTypes.bool,
};

OrderProductBox.defaultProps = {
  modifiers: [],
  isReview: false,
  isRegistered: false,
  removeReview: null,
  readReview: null,
  writeReview: null,
  editEntry: null,
  removeEntry: null,
  simpleView: false,
  showCode: false,
  shippingDate: null,
  indexRatingStar: null,
  isReturn: false,
  isOrderFlow: false,
  printLabelBtn: null,
  printFormBtn: null,
  expectedQuantity: null,
  handleHowItWorksBtn: () => {},
  imageFormat: 'thumbnail',
  hasRecipes: false,
};

export default OrderProductBox;
